import React, { useEffect, useMemo, useState, useCallback, useRef, useLayoutEffect, forwardRef, memo } from 'react';
import {
  Search,
  List,
  CalendarDays,
  Type,
  Image as ImageIcon,
  Mic,
  Activity,
  ListChecks,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  X,
} from 'lucide-react';
import { useJournalIndex } from '../hooks/useJournalIndex';
import { useJournalSearch, MIN_SEARCH_CHARS } from '../hooks/useJournalSearch';

const SAGE = '#7C9A82';
const RENDER_CHUNK = 60; // progressive-render window size; search still spans full history

const FILTERS = [
  { key: 'text', label: 'Text', icon: Type },
  { key: 'images', label: 'Photos', icon: ImageIcon },
  { key: 'audio', label: 'Audio', icon: Mic },
  { key: 'echoes', label: 'Echoes', icon: Activity },
  { key: 'reflections', label: 'Reflections', icon: ListChecks },
];

const DAY_MS = 86_400_000;

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseISO(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatDateLong(iso) {
  const d = parseISO(iso);
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatMonthLong(year, month) {
  return new Date(year, month, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function entryMatchesFilters(entry, active) {
  if (!active.size) return true;
  if (active.has('text')        && entry.snippet && entry.snippet.length > 0) return true;
  if (active.has('images')      && entry.imageCount      > 0) return true;
  if (active.has('audio')       && entry.voiceCount      > 0) return true;
  if (active.has('echoes')      && entry.echoCount       > 0) return true;
  if (active.has('reflections') && entry.reflectionCount > 0) return true;
  return false;
}

function entryMatchesQuery(entry, q) {
  if (!q) return true;
  const needle = q.toLowerCase();
  if (entry.date.includes(needle)) return true;
  if (formatDateLong(entry.date).toLowerCase().includes(needle)) return true;
  if (entry.weekday && entry.weekday.toLowerCase().includes(needle)) return true;
  if (entry.snippet && entry.snippet.toLowerCase().includes(needle)) return true;
  if (Array.isArray(entry.tags) && entry.tags.some(t => (t || '').toLowerCase().includes(needle))) return true;
  return false;
}

function useDebounced(value, delay = 120) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// ————————————————————————————————————————————————————————————————
// Sub-components
// ————————————————————————————————————————————————————————————————

function SidebarHeader({ onNewEntry, onCloseMobile }) {
  return (
    <div className="flex items-center justify-between px-5 pt-5 pb-3">
      <h2 className="text-xl font-bold text-stone-800 tracking-tight">Journals</h2>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={onNewEntry}
          className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 hover:bg-emerald-100/70 transition-colors"
          aria-label="Open today's entry"
        >
          <Plus size={14} strokeWidth={2.25} />
          New
        </button>
        {onCloseMobile && (
          <button
            type="button"
            onClick={onCloseMobile}
            className="md:hidden p-1.5 rounded-full text-stone-500 hover:bg-stone-100"
            aria-label="Close entries panel"
          >
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

function SearchStatus({ status }) {
  if (!status) return null;
  if (status === 'searching') {
    return (
      <div
        className="mt-2 mx-3 h-0.5 rounded-full bg-stone-200/80 overflow-hidden"
        role="status"
        aria-label="Searching inside entries"
      >
        <div className="h-full w-1/3 rounded-full bg-stone-400/70 motion-safe:animate-pulse" />
      </div>
    );
  }
  return (
    <p role="status" className="mt-2 px-3 text-xs text-stone-400">
      {status === 'failed'
        ? "Couldn't search inside entries just now. Showing date and tag matches."
        : 'Showing the 500 most recent matches.'}
    </p>
  );
}

function Excerpt({ hit }) {
  const { excerpt = '', section, matchStart, matchLength } = hit;
  const valid =
    Number.isInteger(matchStart) &&
    Number.isInteger(matchLength) &&
    matchStart >= 0 &&
    matchLength > 0 &&
    matchStart + matchLength <= excerpt.length;
  return (
    <p className="text-[13px] text-stone-600 leading-snug line-clamp-3">
      {section && (
        <span className="mr-1.5 text-[9px] font-semibold text-stone-400 uppercase tracking-[0.18em]">
          {section}
        </span>
      )}
      {valid ? (
        <>
          {excerpt.slice(0, matchStart)}
          <mark className="bg-amber-100 text-stone-800 font-semibold rounded-sm px-0.5">
            {excerpt.slice(matchStart, matchStart + matchLength)}
          </mark>
          {excerpt.slice(matchStart + matchLength)}
        </>
      ) : (
        excerpt
      )}
    </p>
  );
}

function SearchInput({ value, onChange, status }) {
  const inputRef = useRef(null);
  return (
    <div className="px-5 pb-3">
      <div className="relative">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Search keyword, tag, or date…"
          aria-label="Search journal entries"
          className={`w-full bg-stone-50 border border-stone-200/70 rounded-full pl-10 ${value ? 'pr-10' : 'pr-4'} py-2.5 text-sm text-stone-700 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-300/60 focus:border-stone-300 transition-shadow`}
        />
        {value && (
          <button
            type="button"
            onClick={() => { onChange(''); inputRef.current?.focus(); }}
            aria-label="Clear search"
            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-full text-stone-400 hover:text-stone-600 hover:bg-stone-200/60 transition-colors"
          >
            <X size={15} />
          </button>
        )}
      </div>
      <SearchStatus status={status} />
    </div>
  );
}

function ViewToggle({ view, onChange }) {
  const tab = (id, label, Icon) => {
    const active = view === id;
    return (
      <button
        key={id}
        type="button"
        role="tab"
        aria-selected={active}
        onClick={() => onChange(id)}
        className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all ${
          active
            ? 'bg-white text-stone-800 shadow-[0_1px_2px_rgba(45,41,38,0.08)]'
            : 'text-stone-500 hover:text-stone-700'
        }`}
      >
        <Icon size={15} strokeWidth={1.9} />
        {label}
      </button>
    );
  };
  return (
    <div className="px-5 pb-3">
      <div role="tablist" aria-label="View mode" className="flex items-center gap-1 bg-stone-100/70 rounded-full p-1">
        {tab('list', 'List', List)}
        {tab('calendar', 'Calendar', CalendarDays)}
      </div>
    </div>
  );
}

function FilterBar({ active, onToggle }) {
  return (
    <div className="px-5 pb-4">
      <div className="flex flex-wrap gap-1.5">
        {FILTERS.map(({ key, label, icon: Icon }) => {
          const on = active.has(key);
          return (
            <button
              key={key}
              type="button"
              onClick={() => onToggle(key)}
              aria-pressed={on}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                on
                  ? 'bg-stone-900 text-white border-stone-900 shadow-[0_1px_2px_rgba(45,41,38,0.18)]'
                  : 'bg-stone-50 text-stone-500 border-stone-200/70 hover:bg-stone-100/70 hover:text-stone-700 hover:border-stone-300'
              }`}
            >
              <Icon size={13} strokeWidth={on ? 2.2 : 1.9} />
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BadgeIcon({ icon: Icon, count }) {
  if (!count) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-stone-500">
      <Icon size={12} strokeWidth={1.9} />
      {count}
    </span>
  );
}

function EntryCard({ entry, selected, onSelect, hit }) {
  const { date, weekday, snippet, tags, imageCount, voiceCount, echoCount, reflectionCount } = entry;
  const badges = imageCount + voiceCount + echoCount + reflectionCount > 0;
  const shownTags = Array.isArray(tags) ? tags.slice(0, 2) : [];

  return (
    <button
      type="button"
      onClick={() => onSelect(date)}
      className={`text-left w-full rounded-2xl border transition-all px-4 py-3.5 focus:outline-none ${
        selected
          ? 'bg-white border-transparent ring-2 ring-[color:var(--sage)] shadow-[0_1px_3px_rgba(45,41,38,0.06)]'
          : 'bg-white/70 border-stone-200/60 hover:bg-white hover:border-stone-200 hover:shadow-[0_1px_3px_rgba(45,41,38,0.05)]'
      }`}
      style={{ ['--sage']: SAGE }}
      aria-current={selected ? 'true' : undefined}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-semibold text-stone-800 tracking-tight">
          {formatDateLong(date)}
        </span>
        <span className="text-[10px] font-semibold text-stone-400 uppercase tracking-[0.18em]">
          {weekday}
        </span>
      </div>

      {hit ? (
        <Excerpt hit={hit} />
      ) : (
        snippet && (
          <p className="text-[13px] text-stone-600 leading-snug line-clamp-2">{snippet}</p>
        )
      )}

      {(badges || shownTags.length > 0) && (
        <div className="mt-2.5 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <BadgeIcon icon={ImageIcon}  count={imageCount} />
            <BadgeIcon icon={Mic}        count={voiceCount} />
            <BadgeIcon icon={Activity}   count={echoCount} />
            <BadgeIcon icon={ListChecks} count={reflectionCount} />
          </div>
          {shownTags.length > 0 && (
            <div className="flex items-center gap-1">
              {shownTags.map(t => (
                <span
                  key={t}
                  className="text-[9px] font-semibold text-stone-400 uppercase tracking-[0.18em]"
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </button>
  );
}

function EntryList({
  entries,
  totalCount,
  selectedDate,
  onSelectDate,
  onLoadMore,
  isLoading,
  isEmpty,
  hitsByDate,
}) {
  const sentinelRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    // If we've already revealed the whole filtered set, no sentinel work needed.
    if (entries.length >= totalCount) return;
    const node = sentinelRef.current;
    if (!node) return;
    const root = scrollRef.current || null;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) onLoadMore(); },
      { root, rootMargin: '400px 0px', threshold: 0 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [entries.length, totalCount, onLoadMore]);

  if (isLoading) {
    return (
      <div className="px-5 pb-6 space-y-2.5">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="h-[86px] rounded-2xl bg-stone-100/60 animate-pulse" />
        ))}
      </div>
    );
  }
  if (isEmpty) {
    return (
      <div className="px-6 pb-8 pt-2 text-center">
        <p className="text-sm text-stone-500 leading-relaxed">
          Nothing matches this view.
        </p>
        <p className="text-xs text-stone-400 mt-1">
          Try clearing search or filters.
        </p>
      </div>
    );
  }

  const moreToShow = entries.length < totalCount;

  return (
    <div ref={scrollRef} className="px-5 pb-6 space-y-2.5 overflow-y-auto flex-1 min-h-0 scrollbar-hide">
      {entries.map(entry => (
        <EntryCard
          key={entry.date}
          entry={entry}
          hit={hitsByDate?.get(entry.date)}
          selected={entry.date === selectedDate}
          onSelect={onSelectDate}
        />
      ))}
      {moreToShow && (
        <div
          ref={sentinelRef}
          className="h-16 flex items-center justify-center"
          aria-hidden="true"
        >
          <div className="flex gap-1.5">
            {[0, 1, 2].map(i => (
              <span
                key={i}
                className="w-1.5 h-1.5 rounded-full bg-stone-300 animate-pulse"
                style={{ animationDelay: `${i * 120}ms` }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function toISOFromDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Build cells for one month (only in-month days). Neighboring months are
// intentionally left as empty slots so each month reads as its own island
// rather than a smeared grid with faded next/prev filler.
function buildMonthCells(year, month) {
  const startWeekday = new Date(year, month, 1).getDay(); // 0 = Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push({ day, iso: toISOFromDate(new Date(year, month, day)) });
  }
  return cells;
}

// One month block. Memoized + rendered with `content-visibility: auto` so
// off-screen months skip layout and paint — the modern-native equivalent of
// virtualization, no library needed.
const MonthBlock = memo(forwardRef(function MonthBlock(
  { year, month, entryByDate, selectedDate, todayIso, onSelectDate },
  ref
) {
  const cells = useMemo(() => buildMonthCells(year, month), [year, month]);
  const dayCellBase =
    'relative h-9 w-9 mx-auto flex items-center justify-center text-sm rounded-xl transition-colors overflow-hidden';

  return (
    <section
      ref={ref}
      data-month={`${year}-${month}`}
      className="pt-2"
      style={{ contentVisibility: 'auto', containIntrinsicSize: '0 280px' }}
    >
      <h3 className="text-sm font-bold text-stone-800 tracking-tight mb-2 px-0.5">
        {formatMonthLong(year, month)}
      </h3>
      <div className="grid grid-cols-7 gap-y-1.5">
        {cells.map((cell, i) => {
          if (!cell) return <div key={`e-${i}`} className="h-9 w-9" aria-hidden="true" />;
          const entry     = entryByDate.get(cell.iso);
          const isSelected = cell.iso === selectedDate;
          const isToday    = cell.iso === todayIso;
          const hasEntry   = !!entry;
          const isFuture   = cell.iso > todayIso;
          const thumb      = entry?.latestImage || null;

          let cls;
          if (isSelected) {
            cls = `${dayCellBase} bg-stone-900 text-white font-semibold`;
          } else if (isFuture) {
            cls = `${dayCellBase} text-stone-300 cursor-not-allowed`;
          } else if (thumb) {
            // Image thumb takes over the cell; text sits on a soft gradient
            // so the day number stays legible over any photo.
            // Photo days render one step larger (40px vs 36px); the negative
            // vertical margin keeps every calendar row the same height.
            cls = `${dayCellBase.replace('h-9 w-9', 'h-10 w-10 -my-0.5')} text-white font-semibold`;
          } else if (isToday) {
            cls = `${dayCellBase} text-stone-800 font-semibold border border-stone-300 hover:border-stone-500`;
          } else {
            cls = `${dayCellBase} text-stone-700 hover:bg-stone-100`;
          }

          return (
            <button
              key={cell.iso}
              type="button"
              onClick={() => !isFuture && onSelectDate(cell.iso)}
              disabled={isFuture}
              className={cls}
              aria-label={`${formatDateLong(cell.iso)}${hasEntry ? ', has entry' : ''}${isSelected ? ', selected' : ''}`}
              aria-current={isSelected ? 'date' : undefined}
            >
              {thumb && !isSelected && (
                <>
                  <img
                    src={thumb}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <span
                    aria-hidden="true"
                    className="absolute inset-0 bg-gradient-to-t from-stone-900/70 via-stone-900/25 to-stone-900/10"
                  />
                </>
              )}
              <span className="relative z-[1] leading-none">{cell.day}</span>
              {hasEntry && !thumb && (
                <span
                  className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 w-[6px] h-[6px] rounded-full ${
                    isSelected ? 'bg-white' : ''
                  }`}
                  style={isSelected ? undefined : { backgroundColor: '#5A7A60' }}
                />
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}));

// Continuous multi-year calendar. Renders every month between the user's
// earliest journaled month and today (widened to include the selected date
// if it lies outside that span), newest at the top. Scroll to travel back
// in time.
function ScrollableCalendar({ fullEntries = [], visibleEntries = [], selectedDate, onSelectDate }) {
  const scrollRef = useRef(null);
  const selectedMonthRef = useRef(null);
  const [visibleYear, setVisibleYear] = useState(() => {
    const d = parseISO(selectedDate) || new Date();
    return d.getFullYear();
  });

  // O(1) date → entry lookup. Cells use this both to know a date has content
  // (was: entryDates Set) AND to pull the day's latest image for thumbnails.
  const entryByDate = useMemo(() => {
    const map = new Map();
    for (const e of visibleEntries) map.set(e.date, e);
    return map;
  }, [visibleEntries]);

  // Range of months to render: [earliest full-history entry, today], widened
  // to include the selected date if it falls outside. Newest first.
  const months = useMemo(() => {
    const today = new Date();
    let minY = today.getFullYear(), minM = today.getMonth();
    let maxY = today.getFullYear(), maxM = today.getMonth();

    const consider = (y, m) => {
      if (y < minY || (y === minY && m < minM)) { minY = y; minM = m; }
      if (y > maxY || (y === maxY && m > maxM)) { maxY = y; maxM = m; }
    };

    for (const e of fullEntries) {
      const d = parseISO(e.date);
      if (d) consider(d.getFullYear(), d.getMonth());
    }
    const sel = parseISO(selectedDate);
    if (sel) consider(sel.getFullYear(), sel.getMonth());

    const list = [];
    let y = maxY, m = maxM;
    // Guard against pathological loops — at most 30 years of months.
    for (let guard = 0; guard < 360; guard++) {
      list.push({ year: y, month: m });
      if (y === minY && m === minM) break;
      if (m === 0) { y--; m = 11; } else m--;
    }
    return list;
  }, [fullEntries, selectedDate]);

  const selMonth = useMemo(() => {
    const d = parseISO(selectedDate);
    return d ? { year: d.getFullYear(), month: d.getMonth() } : null;
  }, [selectedDate]);

  const todayIso = todayISO();

  // On mount (view switch to Calendar) jump to the selected month without
  // animation — the calendar should already be sitting on wherever the user
  // is looking, no first-frame scroll.
  useLayoutEffect(() => {
    const node = selectedMonthRef.current;
    const scroller = scrollRef.current;
    if (!node || !scroller) return;
    scroller.scrollTop = node.offsetTop - scroller.offsetTop - 8;
    // Mount-only — after that, user scrolling is authoritative.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Today" navigates to today's entry, not just to the top of the list.
  // Scrolling alone reads as broken on mobile, where the calendar lives in a
  // drawer: the drawer stays open over the journal, so the one thing the user
  // asked for — seeing today — visibly does not happen. Selecting the date goes
  // through the same handler as tapping a day cell, which also closes the
  // drawer. The scroll stays because today's month is rendered first, so the
  // list should land there too when the drawer is reopened.
  const scrollToToday = useCallback(() => {
    onSelectDate?.(todayIso);
    const scroller = scrollRef.current;
    if (!scroller) return;
    scroller.scrollTo({ top: 0, behavior: 'smooth' });
  }, [onSelectDate, todayIso]);

  const scrollToMonth = useCallback((year, month) => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const node = scroller.querySelector(`[data-month="${year}-${month}"]`);
    if (!node) return;
    scroller.scrollTo({ top: node.offsetTop - scroller.offsetTop - 8, behavior: 'smooth' });
  }, []);

  // "End of year" = newest month of that year that actually exists in the
  // rendered range (December for any past year; today's month for the current
  // year since future months aren't drawn). "Start of year" = oldest month of
  // that year (January in general; whatever the earliest journaled month is
  // if the user's history begins mid-year).
  const scrollToYearEdge = useCallback((year, edge) => {
    const list = edge === 'end'
      ? months                  // newest-first: first match = newest month
      : [...months].reverse();  // reverse for oldest-first
    const target = list.find(m => m.year === year);
    if (target) scrollToMonth(target.year, target.month);
  }, [months, scrollToMonth]);

  // Track which year is currently at the top of the viewport. We watch every
  // month block with a single IntersectionObserver (cheap) and pick whichever
  // intersecting block sits closest to the scroller's top edge. rootMargin
  // trims to just the topmost slice so the answer flips crisply while
  // scrolling, not lazily.
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;

    const io = new IntersectionObserver((entries) => {
      // The "current" year is whichever month sits at the very top of the
      // viewport. That is the intersecting block whose top is smallest —
      // possibly *negative*, since a newer month entering from above has its
      // top above the scroller before it's fully in view. We still gate on
      // "some pixels visible" via bottom > 0 so we don't pick a block that
      // has scrolled entirely past.
      const scrRect = scroller.getBoundingClientRect();
      let bestTop = Infinity;
      let bestYear = null;
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        const top    = e.boundingClientRect.top    - scrRect.top;
        const bottom = e.boundingClientRect.bottom - scrRect.top;
        if (bottom <= 0) continue;
        if (top < bestTop) {
          bestTop = top;
          const attr = e.target.getAttribute('data-month') || '';
          const y = parseInt(attr.split('-')[0], 10);
          if (Number.isFinite(y)) bestYear = y;
        }
      }
      if (bestYear !== null) setVisibleYear(bestYear);
    }, {
      root: scroller,
      // Observe a top strip so we react before a block is fully in view.
      rootMargin: '0px 0px -75% 0px',
      threshold: [0, 0.01, 0.5, 1],
    });

    const nodes = scroller.querySelectorAll('[data-month]');
    nodes.forEach(n => io.observe(n));
    return () => io.disconnect();
  }, [months]);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Weekday header + Today shortcut */}
      <div className="px-5 flex items-center justify-between pb-1">
        <div className="grid grid-cols-7 gap-x-0 flex-1 mr-2 text-[10px] font-semibold text-stone-400 tracking-widest uppercase text-center">
          {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => <div key={d}>{d}</div>)}
        </div>
        <button
          type="button"
          onClick={scrollToToday}
          className="text-[11px] font-semibold text-stone-500 hover:text-stone-800 transition-colors flex-shrink-0"
        >
          Today
        </button>
      </div>

      <div className="relative flex-1 min-h-0">
        <div
          ref={scrollRef}
          className="absolute inset-0 px-5 pb-6 overflow-y-auto scrollbar-hide"
        >
          {months.map(({ year, month }) => {
            const isSelectedMonth = selMonth && selMonth.year === year && selMonth.month === month;
            return (
              <MonthBlock
                key={`${year}-${month}`}
                ref={isSelectedMonth ? selectedMonthRef : null}
                year={year}
                month={month}
                entryByDate={entryByDate}
                selectedDate={selectedDate}
                todayIso={todayIso}
                onSelectDate={onSelectDate}
              />
            );
          })}
        </div>

        {/* Floating year-boundary shortcuts. Anchored to the scroll surface's
            top and bottom so they always sit within thumb reach. Track the
            year at the top of the viewport (visibleYear). Top pill jumps to
            December of that year (newer; up in DOM), bottom pill jumps to
            January (older; down in DOM). */}
        <YearJumpPill
          position="top"
          year={visibleYear}
          onJump={() => scrollToYearEdge(visibleYear, 'end')}
          label={`End of ${visibleYear}`}
        />
        <YearJumpPill
          position="bottom"
          year={visibleYear}
          onJump={() => scrollToYearEdge(visibleYear, 'start')}
          label={`Start of ${visibleYear}`}
        />
      </div>
    </div>
  );
}

function YearJumpPill({ position, year, onJump, label }) {
  const anchorCls = position === 'top'
    ? 'top-2'
    : 'bottom-3';
  return (
    <button
      type="button"
      onClick={onJump}
      aria-label={label}
      className={`absolute left-1/2 -translate-x-1/2 ${anchorCls} inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/85 backdrop-blur-md border border-stone-200/70 shadow-[0_2px_6px_rgba(45,41,38,0.10)] text-[11px] font-semibold text-stone-700 hover:bg-white hover:text-stone-900 transition-all opacity-90 hover:opacity-100 pointer-events-auto`}
    >
      <span className="text-stone-400">{position === 'top' ? '↑' : '↓'}</span>
      <span>{label}</span>
    </button>
  );
}

// ————————————————————————————————————————————————————————————————
// Main
// ————————————————————————————————————————————————————————————————

export default function JournalSidebar({
  userId,
  currentDate,
  onSelectDate,
  onNewEntry,
  collapsed,
  onToggleCollapsed,
  mobileOpen = false,
  onMobileOpenChange,
}) {
  const [view, setView] = useState('list');
  const [queryRaw, setQueryRaw] = useState('');
  const [filters, setFilters] = useState(() => new Set());
  const [visibleCount, setVisibleCount] = useState(RENDER_CHUNK);

  const query = useDebounced(queryRaw, 120);
  const { data: entries = [], isLoading } = useJournalIndex(userId);

  // Full-text search inside entries runs server-side (bodies are encrypted),
  // on a slower debounce than the instant local date/tag/snippet match.
  const serverQuery = useDebounced(queryRaw.trim(), 300);
  const search = useJournalSearch(userId, serverQuery);
  const serverActive = [...query.trim()].length >= MIN_SEARCH_CHARS;
  // Last settled results stay on screen while the next query is in flight, so
  // the list does not flash empty on every keystroke.
  const lastHitsRef = useRef(new Map());
  const hitsByDate = useMemo(() => {
    if (!serverActive) return new Map();
    if (!search.data) return lastHitsRef.current;
    const results = Array.isArray(search.data.results) ? search.data.results : [];
    return new Map(results.map(h => [h.date, h]));
  }, [serverActive, search.data]);
  useEffect(() => {
    lastHitsRef.current = hitsByDate;
  }, [hitsByDate]);
  const searchStatus = !serverActive
    ? null
    : search.isError
      ? 'failed'
      : search.isFetching || serverQuery !== queryRaw.trim()
        ? 'searching'
        : search.data?.truncated
          ? 'truncated'
          : null;

  // Search + filter run against the FULL history so results are complete.
  // Only the render pipeline is chunked (see visibleCount → EntryList).
  const visibleEntries = useMemo(() => {
    const q = query.trim();
    return entries.filter(
      e => (hitsByDate.has(e.date) || entryMatchesQuery(e, q)) && entryMatchesFilters(e, filters)
    );
  }, [entries, query, filters, hitsByDate]);

  // Reset the render window whenever the filtered set changes shape — so a new
  // search doesn't inherit a scroll position deep into the previous result.
  useEffect(() => {
    setVisibleCount(RENDER_CHUNK);
  }, [query, filters]);

  const renderedEntries = useMemo(
    () => visibleEntries.slice(0, visibleCount),
    [visibleEntries, visibleCount]
  );

  const loadMore = useCallback(() => {
    setVisibleCount(c => Math.min(c + RENDER_CHUNK, visibleEntries.length));
  }, [visibleEntries.length]);

  const toggleFilter = useCallback((key) => {
    setFilters(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const handleSelectDate = useCallback((date) => {
    onSelectDate(date);
    onMobileOpenChange?.(false);
  }, [onSelectDate, onMobileOpenChange]);

  const closeMobile = useCallback(() => onMobileOpenChange?.(false), [onMobileOpenChange]);

  // ————————————————————————————————
  // Rail (desktop collapsed) — a thin sliver with just the expand chevron
  // ————————————————————————————————
  if (collapsed) {
    return (
      <>
        {/* Desktop rail */}
        <div className="hidden md:flex flex-col items-center w-8 flex-shrink-0 self-stretch">
          <div className="sticky top-[92px] py-1">
            <button
              type="button"
              onClick={onToggleCollapsed}
              className="p-2 rounded-full bg-white/80 border border-stone-200/60 text-stone-500 hover:text-stone-700 hover:shadow-sm transition-all"
              aria-label="Show entries panel"
              aria-expanded="false"
            >
              <PanelLeftOpen size={16} strokeWidth={1.9} />
            </button>
          </div>
        </div>

        {/* Mobile drawer — opened from the app header's Entries pill */}
        <MobileDrawer
          open={mobileOpen}
          onClose={closeMobile}
          currentDate={currentDate}
          onSelectDate={handleSelectDate}
          onNewEntry={onNewEntry}
          view={view}
          setView={setView}
          queryRaw={queryRaw}
          setQueryRaw={setQueryRaw}
          filters={filters}
          toggleFilter={toggleFilter}
          fullEntries={entries}
          visibleEntries={visibleEntries}
          renderedEntries={renderedEntries}
          onLoadMore={loadMore}
          isLoading={isLoading}
          hitsByDate={hitsByDate}
          searchStatus={searchStatus}
        />
      </>
    );
  }

  // (First branch's MobileDrawer already receives the right props via the shared shape below.)
  // ————————————————————————————————
  // Expanded: desktop inline panel + mobile drawer
  // ————————————————————————————————
  const panel = (
    <>
      <SidebarHeader onNewEntry={onNewEntry} onCloseMobile={mobileOpen ? closeMobile : null} />
      <SearchInput value={queryRaw} onChange={setQueryRaw} status={searchStatus} />
      <ViewToggle view={view} onChange={setView} />
      <FilterBar active={filters} onToggle={toggleFilter} />
      {view === 'list' ? (
        <EntryList
          entries={renderedEntries}
          totalCount={visibleEntries.length}
          selectedDate={currentDate}
          onSelectDate={handleSelectDate}
          onLoadMore={loadMore}
          isLoading={isLoading}
          isEmpty={!isLoading && visibleEntries.length === 0}
          hitsByDate={hitsByDate}
        />
      ) : (
        <ScrollableCalendar
          fullEntries={entries}
          visibleEntries={visibleEntries}
          selectedDate={currentDate}
          onSelectDate={handleSelectDate}
        />
      )}
    </>
  );

  return (
    <>
      {/* Desktop panel — the outer <aside> stretches to match the journal
          column's height (`self-stretch` overrides parent items-start), so
          the two columns are visually parallel no matter how tall the entry
          gets. The inner card is `sticky`, viewport-clamped, so the nav
          stays anchored to the top of the screen as the user scrolls the
          journal below. */}
      <aside
        className="hidden md:block w-[320px] flex-shrink-0 self-stretch"
        aria-label="Journal entries"
      >
        <div className="sticky top-[80px] relative flex flex-col rounded-3xl bg-white/60 backdrop-blur-sm border border-stone-200/60 shadow-[0_1px_3px_rgba(45,41,38,0.05)] h-[calc(100dvh-100px)]">
          {panel}
          <button
            type="button"
            onClick={onToggleCollapsed}
            className="absolute top-1/2 -right-3 -translate-y-1/2 w-6 h-12 rounded-r-full bg-white border border-stone-200/70 shadow-sm text-stone-400 hover:text-stone-600 hover:shadow flex items-center justify-center transition-all"
            aria-label="Hide entries panel"
            aria-expanded="true"
          >
            <PanelLeftClose size={14} strokeWidth={1.9} />
          </button>
        </div>
      </aside>

      {/* Mobile drawer — opened from the app header's Entries pill */}
      <MobileDrawer
        open={mobileOpen}
        onClose={closeMobile}
        currentDate={currentDate}
        onSelectDate={handleSelectDate}
        onNewEntry={onNewEntry}
        view={view}
        setView={setView}
        queryRaw={queryRaw}
        setQueryRaw={setQueryRaw}
        filters={filters}
        toggleFilter={toggleFilter}
        fullEntries={entries}
        visibleEntries={visibleEntries}
        renderedEntries={renderedEntries}
        onLoadMore={loadMore}
        isLoading={isLoading}
        hitsByDate={hitsByDate}
        searchStatus={searchStatus}
      />
    </>
  );
}

function MobileDrawer({
  open, onClose,
  currentDate, onSelectDate, onNewEntry,
  view, setView,
  queryRaw, setQueryRaw,
  filters, toggleFilter,
  fullEntries, visibleEntries, renderedEntries, onLoadMore, isLoading,
  hitsByDate, searchStatus,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <>
      <div
        className={`md:hidden fixed inset-0 z-40 bg-stone-900/30 backdrop-blur-sm transition-opacity duration-300 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`md:hidden fixed inset-y-0 left-0 z-50 w-[85vw] max-w-[360px] flex flex-col bg-[#FBF8F1] border-r border-stone-200/70 shadow-2xl transition-transform duration-300 ease-out ${open ? 'translate-x-0' : '-translate-x-full'}`}
        role="dialog"
        aria-modal="true"
        aria-label="Journal entries"
      >
        <SidebarHeader onNewEntry={onNewEntry} onCloseMobile={onClose} />
        <SearchInput value={queryRaw} onChange={setQueryRaw} status={searchStatus} />
        <ViewToggle view={view} onChange={setView} />
        <FilterBar active={filters} onToggle={toggleFilter} />
        {view === 'list' ? (
          <EntryList
            entries={renderedEntries}
            totalCount={visibleEntries.length}
            selectedDate={currentDate}
            onSelectDate={onSelectDate}
            onLoadMore={onLoadMore}
            isLoading={isLoading}
            isEmpty={!isLoading && visibleEntries.length === 0}
            hitsByDate={hitsByDate}
          />
        ) : (
          <ScrollableCalendar
            fullEntries={fullEntries}
            visibleEntries={visibleEntries}
            selectedDate={currentDate}
            onSelectDate={onSelectDate}
          />
        )}
      </aside>
    </>
  );
}
